<template>
  <b-modal
    ref="modal"
    class="modal-basic-info"
    :content-class="
      $device.isMobile
        ? 'mobile-fullpage border-0'
        : 'rounded-10 border-0 overflow-hidden'
    "
    :dialog-class="$device.isMobile ? 'mobile-dialog' : 'pc-dialog'"
    :size="$device.isMobile ? 'fullscreen' : 'sm'"
    hide-footer
    hide-header
    no-close-on-backdrop
    no-close-on-esc
    :visible="show"
    centered
  >
    <div class="basic-info" :class="{'is-mobile': $device.isMobile}">
      <div class="header">
        <h2>{{ $t('基本資訊') }}</h2>
      </div>

      <div class="content">
        <!-- 暱稱 -->
        <div class="field">
          <label class="field-label" for="basic-info-nickname">
            {{ $t('該怎麼稱呼你？') }}
          </label>
          <b-form-input
            id="basic-info-nickname"
            ref="nickname"
            v-model="$v.nickname.$model"
            class="field-input"
            name="nickname"
            type="text"
            :placeholder="$t('請輸入')"
            :state="isNicknameValid"
            @input="onNicknameInput"
          ></b-form-input>
          <b-form-invalid-feedback :state="isNicknameValid" class="mt-0">
            <span v-if="!$v.nickname.required" class="error">
              {{ $t('請輸入暱稱') }}
            </span>
            <span v-else-if="!$v.nickname.maxLength" class="error">
              {{ $t('請輸入{length}個字以內的暱稱', {length: 15}) }}
            </span>
            <span v-else-if="backendError" class="error">
              {{ backendError }}
            </span>
          </b-form-invalid-feedback>
        </div>

        <!-- 有學過圍棋嗎？ -->
        <div class="field">
          <span class="field-label">{{ $t('有學過圍棋嗎？') }}</span>
          <div
            class="radio-card"
            :class="{active: hasPlayed === 'hasPlay'}"
            @click="hasPlayed = 'hasPlay'"
          >
            <b-form-radio v-model="hasPlayed" value="hasPlay">
              {{ $t('有') }}
            </b-form-radio>
          </div>
          <div
            class="radio-card"
            :class="{active: hasPlayed === 'noPlay'}"
            @click="hasPlayed = 'noPlay'"
          >
            <b-form-radio v-model="hasPlayed" value="noPlay">
              {{ $t('沒有') }}
            </b-form-radio>
          </div>
        </div>

        <hr class="divider" />

        <!-- 好友邀請碼 -->
        <div class="field">
          <label class="field-label" for="basic-info-invite-code">
            {{ $t('好友邀請碼（選填）') }}
          </label>
          <b-form-input
            id="basic-info-invite-code"
            ref="inviteCode"
            v-model="inviteCode"
            class="field-input"
            name="invite-code"
            type="text"
            :placeholder="$t('請輸入')"
            :state="inviteCodeError ? false : null"
            @input="onInviteCodeInput"
          ></b-form-input>
          <b-form-invalid-feedback
            :state="inviteCodeError ? false : null"
            class="mt-0"
          >
            <span v-if="inviteCodeError" class="error">
              {{ inviteCodeError }}
            </span>
          </b-form-invalid-feedback>
        </div>
      </div>

      <div class="footer">
        <b-button
          variant="primary"
          class="next-btn"
          :disabled="isLoading"
          @click="submit"
        >
          {{ $t('下一步') }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {required, maxLength} from 'vuelidate/lib/validators';
import userService from '@/services/user';

export default {
  props: {
    show: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      nickname: '',
      hasPlayed: null,
      inviteCode: '',
      isNicknameValid: null,
      backendError: '',
      inviteCodeError: '',
      isLoading: false,
    };
  },
  validations: {
    nickname: {
      required,
      maxLength: maxLength(15),
    },
  },
  methods: {
    onNicknameInput() {
      this.isNicknameValid = null;
      this.backendError = '';
    },
    onInviteCodeInput() {
      this.inviteCodeError = '';
    },
    async submit() {
      if (this.isLoading) return;

      this.$v.$touch();
      if (this.$v.$invalid) {
        this.isNicknameValid = false;
        this.$refs.nickname?.focus();
        return;
      }
      if (!this.hasPlayed) {
        this.$Message.error(this.$t('請選擇是否學過圍棋'));
        return;
      }

      try {
        this.isLoading = true;
        this.$store.commit('env/setIsLoading', true);

        // 1) 好友邀請碼（選填）：填了就先驗，失敗時中斷整個流程
        const code = this.inviteCode?.trim();
        let hasAppliedInviteCode = false;
        if (code) {
          try {
            await userService.useInviteCode(code);
            hasAppliedInviteCode = true;
          } catch (err) {
            this.inviteCodeError =
              err.response?.data?.message ||
              this.$t('邀請碼無效，請確認後再試');
            await this.$nextTick();
            this.$refs.inviteCode?.focus();
            return; // 中斷，不更新暱稱、不進入下一階段
          }
        }

        // 2) 邀請碼通過（或未填）→ 更新暱稱
        try {
          await userService.updateUser({
            nickName: this.nickname,
            isActive: true,
          });
        } catch (error) {
          this.isNicknameValid = false;
          if (error.response?.data?.message === 'Incorrect nickName!') {
            this.backendError = '*' + this.$t('該名稱不得使用');
          } else {
            this.backendError = '*' + this.$t('暱稱設定失敗');
          }
          this.$refs.nickname?.focus();
          return;
        }

        // 先 emit 通知父層更新 initialSelected，再呼叫 loginWithJwt 重新整理
        this.$emit('submit', {
          hasPlayed: this.hasPlayed,
          hasAppliedInviteCode,
        });

        await this.$store.dispatch('user/loginWithJwt');
      } finally {
        this.isLoading = false;
        this.$store.commit('env/setIsLoading', false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.pc-dialog) {
  width: 288px;
  max-width: 288px;
  margin: 0 auto;
  .modal-body {
    padding: 0;
  }
}
:deep(.mobile-dialog) {
  width: 100vw;
  max-width: 100vw;
  height: 100vh;
  max-height: 100vh;
  margin: 0;
  .modal-content {
    height: 100vh;
    border-radius: 0;
  }
  .modal-body {
    padding: 0;
    height: 100%;
  }
}

.basic-info {
  display: flex;
  flex-direction: column;
  background: $white;
  color: $font-normal;

  // ── Header ─────────────────────────────────
  .header {
    display: flex;
    align-items: center;
    padding: 8px 8px 8px 16px;
    border-bottom: 1px solid $line-1;
    h2 {
      flex: 1;
      margin: 0;
      font-size: 18px;
      font-weight: 700;
      line-height: 30px;
      color: $font-grayscale-1;
      text-align: left;
    }
  }

  // ── Content ────────────────────────────────
  .content {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 16px;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 8px;
    .field-label {
      font-size: 14px;
      font-weight: 400;
      line-height: 22px;
      color: $font-normal;
      margin: 0;
    }
    .field-input {
      height: 38px;
      padding: 8px 12px;
      border-radius: 4px;
      border: 1px solid $line-1;
      font-size: 14px;
      line-height: 22px;
      &::placeholder {
        color: $font-grayscale-1;
      }
    }
  }

  // ── 圍棋經驗 Radio Cards ─────────────────────
  .radio-card {
    display: flex;
    align-items: center;
    padding: 8px;
    border-radius: 10px;
    background: $bgcontainer;
    cursor: pointer;
    transition: background 0.15s ease;
    &.active {
      background: rgba($primary, 0.08);
    }
    :deep(.custom-radio) {
      margin: 0;
      padding-left: 24px;
      .custom-control-label {
        font-size: 16px;
        line-height: 28px;
        color: $font-normal;
        cursor: pointer;
        &::before,
        &::after {
          top: 6px;
          left: -24px;
          width: 16px;
          height: 16px;
        }
      }
    }
  }

  // ── Divider ────────────────────────────────
  .divider {
    margin: 0;
    border: none;
    border-top: 1px solid $line-1;
  }

  // ── Footer ─────────────────────────────────
  .footer {
    display: flex;
    justify-content: center;
    padding: 8px 16px 16px;
    border-top: 1px solid $line-1;
    .next-btn {
      min-width: 72px;
      height: 38px;
      padding: 5px 12px;
      border-radius: 4px;
      font-size: 16px;
      line-height: 28px;
      font-weight: 400;
    }
  }

  // ── Mobile 整頁版本 ──────────────────────────
  &.is-mobile {
    min-height: 100vh;
    .header {
      justify-content: center;
      padding: 14px 16px;
      h2 {
        text-align: center;
        color: $font-normal;
      }
    }
    .content {
      flex: 1;
    }
    .footer {
      border-top: none;
      padding: 0 16px 24px;
      .next-btn {
        width: 100%;
      }
    }
  }
}
</style>
